import { useCallback, useEffect, useState } from "react";
import { useAudioPlayerStore } from "../store";

interface AudioManagerReturn {
  isAudioReady: boolean;
  duration: number;
  currentTime: number;
  isPlaying: boolean;
  playbackRate: number;
  togglePlay: (playbackRate: number) => Promise<void>;
  seek: (percent: number) => void;
  setPlaybackRate: (rate: number) => void;
  setVolume: (volume: number) => void;
  skip: (seconds: number) => void;
}

export const useAudioManager = (
  trackId: string,
  audioSrc?: string,
  title?: string,
): AudioManagerReturn => {
  const safeTitle = title?.trim() ? title : `جلسه ${trackId}`;
  const activeTrack = useAudioPlayerStore((state) => state.activeTrack);
  const globalIsAudioReady = useAudioPlayerStore((state) => state.isAudioReady);
  const globalDuration = useAudioPlayerStore((state) => state.duration);
  const globalCurrentTime = useAudioPlayerStore((state) => state.currentTime);
  const trackDurations = useAudioPlayerStore((state) => state.trackDurations);
  const trackCurrentTimes = useAudioPlayerStore((state) => state.trackCurrentTimes);
  const trackPlaybackRates = useAudioPlayerStore((state) => state.trackPlaybackRates);
  const globalIsPlaying = useAudioPlayerStore((state) => state.isPlaying);
  const globalPlaybackRate = useAudioPlayerStore((state) => state.playbackRate);
  const toggleTrackPlay = useAudioPlayerStore((state) => state.togglePlay);
  const seekByPercent = useAudioPlayerStore((state) => state.seekByPercent);
  const seekTrackByPercent = useAudioPlayerStore((state) => state.seekTrackByPercent);
  const setGlobalPlaybackRate = useAudioPlayerStore((state) => state.setPlaybackRate);
  const setTrackPlaybackRate = useAudioPlayerStore((state) => state.setTrackPlaybackRate);
  const setGlobalVolume = useAudioPlayerStore((state) => state.setVolume);
  const skipActiveTrack = useAudioPlayerStore((state) => state.skip);
  const [durationCache, setDurationCache] = useState<Record<string, number>>({});

  const isActiveTrack = Boolean(
    audioSrc &&
    activeTrack &&
    activeTrack.id === trackId &&
    activeTrack.src === audioSrc,
  );
  const trackStorageKey = audioSrc ? `${trackId}::${audioSrc}` : "";
  const cachedDuration = trackStorageKey ? (trackDurations[trackStorageKey] ?? 0) : 0;
  const cachedCurrentTime = trackStorageKey ? (trackCurrentTimes[trackStorageKey] ?? 0) : 0;
  const cachedPlaybackRate = trackStorageKey ? (trackPlaybackRates[trackStorageKey] ?? 1) : 1;

  useEffect(() => {
    if (!audioSrc || typeof Audio === "undefined") return;
    if (Object.prototype.hasOwnProperty.call(durationCache, audioSrc)) return;

    let isMounted = true;
    const metadataAudio = new Audio();
    metadataAudio.preload = "metadata";

    const handleLoadedMetadata = () => {
      if (!isMounted) return;
      const nextDuration = Number.isFinite(metadataAudio.duration) ? metadataAudio.duration : 0;
      setDurationCache((prev) => {
        if (prev[audioSrc] === nextDuration) return prev;
        return { ...prev, [audioSrc]: nextDuration };
      });
    };

    const handleError = () => {};

    metadataAudio.addEventListener("loadedmetadata", handleLoadedMetadata);
    metadataAudio.addEventListener("error", handleError);
    metadataAudio.src = audioSrc;

    return () => {
      isMounted = false;
      metadataAudio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      metadataAudio.removeEventListener("error", handleError);
      metadataAudio.src = "";
    };
  }, [audioSrc, durationCache]);

  const togglePlay = useCallback(async (playbackRate: number) => {
    if (!audioSrc) {
      return;
    }

    setGlobalPlaybackRate(playbackRate);
    await toggleTrackPlay({
      id: trackId,
      src: audioSrc,
      title: safeTitle,
    });
  }, [audioSrc, safeTitle, setGlobalPlaybackRate, toggleTrackPlay, trackId]);

  const seek = useCallback((percent: number) => {
    if (!audioSrc) return;

    if (isActiveTrack) {
      seekByPercent(percent);
      return;
    }

    seekTrackByPercent(
      {
        id: trackId,
        src: audioSrc,
        title: safeTitle,
      },
      percent,
    );
  }, [audioSrc, isActiveTrack, safeTitle, seekByPercent, seekTrackByPercent, trackId]);

  const setPlaybackRate = useCallback((rate: number) => {
    if (!audioSrc) return;

    if (isActiveTrack) {
      setGlobalPlaybackRate(rate);
      return;
    }

    setTrackPlaybackRate(
      {
        id: trackId,
        src: audioSrc,
        title: safeTitle,
      },
      rate,
    );
  }, [
    audioSrc,
    isActiveTrack,
    safeTitle,
    setGlobalPlaybackRate,
    setTrackPlaybackRate,
    trackId,
  ]);

  const setVolume = useCallback((volume: number) => {
    setGlobalVolume(volume);
  }, [setGlobalVolume]);

  const skip = useCallback((seconds: number) => {
    if (!isActiveTrack) return;
    skipActiveTrack(seconds);
  }, [isActiveTrack, skipActiveTrack]);

  return {
    isAudioReady: isActiveTrack ? globalIsAudioReady : false,
    duration: isActiveTrack
      ? globalDuration
      : Math.max(cachedDuration, audioSrc ? (durationCache[audioSrc] ?? 0) : 0),
    currentTime: isActiveTrack ? globalCurrentTime : cachedCurrentTime,
    isPlaying: isActiveTrack ? globalIsPlaying : false,
    playbackRate: isActiveTrack ? globalPlaybackRate : cachedPlaybackRate,
    togglePlay,
    seek,
    setPlaybackRate,
    setVolume,
    skip,
  };
};
